export const IMPORT_RUN_STATUSES = [
  '',
  'enqueued',
  'processing',
  'completed',
  'processed',
  'failed',
  'cancelled',
];

export const IMPORT_RUN_MODES = [
  '',
  'full',
  'incremental',
  'replay',
  'dry_run',
];

export const SNAPSHOT_STATES = [
  'empty',
  'partial',
  'quarantined',
  'materialized',
  'dry_run',
  'tombstoned',
];

export const DIFF_OPERATIONS = ['added', 'changed', 'overwritten', 'ignored'];
export const TOMBSTONE_REVIEW_DECISIONS = ['approved', 'suppressed'];
export const DRIFT_REVIEW_ACTIONS = [
  'acknowledge',
  'resolve',
  'dismiss',
  'reopen',
];

const numberValue = value => Number(value || 0);
const arrayValue = (...values) =>
  values.find(value => Array.isArray(value)) || [];

const STATUS_TONES = {
  enqueued: 'warning',
  processing: 'info',
  running: 'info',
  completed: 'positive',
  processed: 'positive',
  failed: 'danger',
  cancelled: 'neutral',
};

export const importRunTone = status => STATUS_TONES[status] || 'neutral';

const SNAPSHOT_STATE_TONES = {
  empty: 'neutral',
  partial: 'warning',
  quarantined: 'danger',
  materialized: 'positive',
  dry_run: 'info',
  tombstoned: 'neutral',
};

export const snapshotStateTone = state =>
  SNAPSHOT_STATE_TONES[state] || 'neutral';

export const normalizeSnapshot = snapshot => ({
  ...snapshot,
  id: snapshot?.id || '',
  snapshotVersion: Number(snapshot?.snapshot_version || 0),
  status: snapshot?.status || '',
  materializedAt: snapshot?.materialized_at || '',
  normalizedDigest: snapshot?.normalized_digest || '',
  materializedDigest: snapshot?.materialized_digest || '',
  previousSnapshotId: snapshot?.previous_snapshot_id || '',
  normalizedPayload: snapshot?.normalized_payload || {},
  diff: snapshot?.diff || {},
  diffCounts: snapshot?.diff_counts || {},
  redaction: snapshot?.redaction || {},
});

export const normalizeSnapshotDetail = detail => ({
  state: detail?.state || '',
  stateReason: detail?.state_reason || '',
  currentSnapshotId: detail?.current_snapshot_id || '',
  latestSnapshotId: detail?.latest_snapshot_id || '',
  timelineCount: Number(detail?.timeline_count || 0),
  diffSource: detail?.diff_source || '',
  providerAcquisition: detail?.provider_acquisition || '',
  snapshots: Array.isArray(detail?.snapshots)
    ? detail.snapshots.map(snapshot => normalizeSnapshot(snapshot))
    : [],
});

export const defaultFieldSuggestion = error => {
  const code = String(
    error?.code || error?.suggestion_code || ''
  ).toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  if (code.includes('duplicate') || message.includes('duplic')) {
    return 'compare_duplicate_before_replay';
  }

  if (code.includes('drift') || code.includes('schema')) {
    return 'update_mapping_ack_drift';
  }

  if (code.includes('missing') || message.includes('ausente')) {
    return 'review_mapping_required_field';
  }

  if (code.includes('invalid') || message.includes('inval')) {
    return 'fix_normalized_value';
  }

  return 'review_trace_and_replay';
};

export const normalizeFieldError = error => ({
  ...error,
  field: error?.field || error?.attribute || error?.path || 'record',
  code: error?.code || 'validation_error',
  message: error?.message || '',
  suggestion:
    error?.suggestion ||
    error?.suggestion_code ||
    error?.suggestionCode ||
    defaultFieldSuggestion(error),
});

export const normalizeMappingFieldRow = row => {
  const errors = Array.isArray(row?.errors)
    ? row.errors.map(normalizeFieldError)
    : [];

  return {
    ...row,
    field: row?.field || '',
    critical: Boolean(row?.critical),
    sourcePaths: arrayValue(row?.source_paths, row?.sourcePaths),
    policy: row?.policy || '',
    normalizedValue:
      row?.normalized_value ??
      row?.normalizedValue ??
      row?.snapshot_value ??
      '',
    trace: row?.trace || {},
    errors,
    diffEntries: arrayValue(row?.diff_entries, row?.diffEntries),
    suggestion:
      row?.suggestion ||
      row?.suggestion_code ||
      row?.suggestionCode ||
      errors[0]?.suggestion ||
      defaultFieldSuggestion(row),
  };
};

export const normalizeReconciliation = reconciliation => ({
  ...reconciliation,
  state: reconciliation?.state || 'unmatched',
  imovel: reconciliation?.imovel || null,
  listings: Array.isArray(reconciliation?.listings)
    ? reconciliation.listings
    : [],
  listingCount: numberValue(
    reconciliation?.listing_count || reconciliation?.listingCount
  ),
  activeListingCount: numberValue(
    reconciliation?.active_listing_count || reconciliation?.activeListingCount
  ),
  duplicate: {
    present: Boolean(reconciliation?.duplicate?.present),
    reason: reconciliation?.duplicate?.reason || '',
    duplicateOfRawRecordId:
      reconciliation?.duplicate?.duplicate_of_raw_record_id ||
      reconciliation?.duplicate?.duplicateOfRawRecordId ||
      '',
  },
});

export const normalizeTombstoneReviewCounts = counts => ({
  approved: numberValue(counts?.approved),
  suppressed: numberValue(counts?.suppressed),
  unreviewed: numberValue(counts?.unreviewed),
});

export const normalizeTombstoneCandidate = candidate => ({
  ...candidate,
  sourceId: candidate?.source_id || candidate?.sourceId || '',
  sourceKey: candidate?.source_key || candidate?.sourceKey || '',
  externalId: candidate?.external_id || candidate?.externalId || '',
  missedRuns: numberValue(candidate?.missed_runs || candidate?.missedRuns),
  graceRuns: numberValue(candidate?.grace_runs || candidate?.graceRuns),
  readyOnNextFullRun: Boolean(
    candidate?.ready_on_next_full_run || candidate?.readyOnNextFullRun
  ),
  reviewStatus:
    candidate?.review?.decision || candidate?.review?.status || 'unreviewed',
  reviewReason: candidate?.review?.reason || '',
  reviewedAt: candidate?.review?.reviewed_at || candidate?.reviewedAt || '',
  providerAcquisition:
    candidate?.review?.provider_acquisition ||
    candidate?.providerAcquisition ||
    'not_attempted',
  sideEffect:
    candidate?.review?.side_effect ||
    candidate?.sideEffect ||
    'source_metadata_only',
  firstMissingRunId:
    candidate?.first_missing_run_id || candidate?.firstMissingRunId || '',
  lastMissingRunId:
    candidate?.last_missing_run_id || candidate?.lastMissingRunId || '',
  previousSnapshotId:
    candidate?.previous_snapshot_id || candidate?.previousSnapshotId || '',
  firstMissingAt:
    candidate?.first_missing_at || candidate?.firstMissingAt || '',
  lastMissingAt: candidate?.last_missing_at || candidate?.lastMissingAt || '',
});

export const normalizeOperationalSummary = summary => {
  const tombstones = summary?.tombstones || {};

  return {
    ...summary,
    sideEffects: Boolean(summary?.side_effects),
    providerCalls: Boolean(summary?.provider_calls),
    tombstones: {
      ...tombstones,
      candidateCount: numberValue(tombstones?.candidate_count),
      readyOnNextFullRunCount: numberValue(
        tombstones?.ready_on_next_full_run_count
      ),
      reviewRequiredCount: numberValue(tombstones?.review_required_count),
      suppressedCount: numberValue(tombstones?.suppressed_count),
      approvedCount: numberValue(tombstones?.approved_count),
      reviewCounts: normalizeTombstoneReviewCounts(tombstones?.review_counts),
      candidates: Array.isArray(tombstones?.candidates)
        ? tombstones.candidates.map(candidate =>
            normalizeTombstoneCandidate(candidate)
          )
        : [],
      reviewOperation: tombstones?.review_operation || '',
      sourceMetadataPath: tombstones?.source_metadata_path || '',
    },
  };
};

export const normalizeRawRecord = record => ({
  ...record,
  id: record?.id || '',
  external_id: record?.external_id || record?.externalId || '',
  source_record_uid: record?.source_record_uid || record?.sourceRecordUid || '',
  status: record?.status || 'unknown',
  snapshotState:
    record?.snapshot_state ||
    record?.snapshotState ||
    record?.snapshot_detail?.state ||
    '',
  snapshotStateReason:
    record?.snapshot_state_reason ||
    record?.snapshotStateReason ||
    record?.snapshot_detail?.state_reason ||
    '',
  snapshotDetail: normalizeSnapshotDetail(record?.snapshot_detail),
  fieldRows: Array.isArray(record?.field_rows)
    ? record.field_rows.map(row => normalizeMappingFieldRow(row))
    : [],
  fieldErrors: Array.isArray(record?.field_errors)
    ? record.field_errors.map(error => normalizeFieldError(error))
    : [],
  reconciliation: normalizeReconciliation(record?.reconciliation),
  redaction: record?.redaction || {},
});

export const snapshotDiffSections = snapshot =>
  DIFF_OPERATIONS.map(operation => ({
    operation,
    entries: Array.isArray(snapshot?.diff?.[operation])
      ? snapshot.diff[operation]
      : [],
  })).filter(section => section.entries.length > 0);

export const normalizeRun = run => ({
  id: run?.id || '',
  token: run?.token || '',
  portal: run?.portal || '',
  status: run?.status || 'unknown',
  importMode: run?.import_mode || run?.importMode || '',
  parserVersion: run?.parser_version || '',
  createdAt: run?.created_at || '',
  updatedAt: run?.updated_at || '',
  completedAt: run?.completed_at || '',
  source: run?.source || {},
  mapping: run?.mapping || {},
  counters: run?.counters || {},
  quality: run?.quality || {},
  rawStatusCounts: run?.raw_status_counts || {},
  retryableRawRecordsCount: Number(run?.retryable_raw_records_count || 0),
  sourceQuarantined: Boolean(run?.source_quarantined),
  active: Boolean(run?.active),
  latestRawRecords: Array.isArray(run?.latest_raw_records)
    ? run.latest_raw_records.map(record => normalizeRawRecord(record))
    : [],
  auditHistory: Array.isArray(run?.audit_history) ? run.audit_history : [],
  operationalSummary: normalizeOperationalSummary(run?.operational_summary),
  redaction: run?.redaction || {},
});

export const normalizeDrift = drift => ({
  ...drift,
  id: drift?.id || '',
  listingImportSourceId:
    drift?.listing_import_source_id || drift?.listingImportSourceId || '',
  listingImportMappingId:
    drift?.listing_import_mapping_id || drift?.listingImportMappingId || '',
  laisImportRunId: drift?.lais_import_run_id || drift?.laisImportRunId || '',
  listingRawRecordId:
    drift?.listing_raw_record_id || drift?.listingRawRecordId || '',
  imovelId: drift?.imovel_id || drift?.imovelId || '',
  externalId: drift?.external_id || drift?.externalId || '',
  driftType: drift?.drift_type || drift?.driftType || '',
  code: drift?.code || '',
  field: drift?.field || '',
  severity: drift?.severity || 'warning',
  blocking: Boolean(drift?.blocking),
  message: drift?.message || '',
  status: drift?.status || 'open',
  resolution: drift?.resolution || '',
  resolutionNote: drift?.resolution_note || drift?.resolutionNote || '',
  detectionEvidence:
    drift?.detection_evidence || drift?.detectionEvidence || {},
  detectedAt: drift?.detected_at || drift?.detectedAt || '',
  acknowledgedAt: drift?.acknowledged_at || drift?.acknowledgedAt || '',
  resolvedAt: drift?.resolved_at || drift?.resolvedAt || '',
  suggestion: defaultFieldSuggestion(drift),
});

export const normalizeDriftsResponse = response => ({
  drifts: Array.isArray(response?.payload)
    ? response.payload.map(item => normalizeDrift(item))
    : [],
  meta: response?.meta || {
    count: 0,
    total_count: 0,
    page: 1,
    per_page: 10,
    total_pages: 0,
    summary: {},
  },
});

export const normalizeDriftReviewResponse = response => ({
  drift: response?.payload ? normalizeDrift(response.payload) : null,
  status: response?.status || '',
  changed: Boolean(response?.changed),
  auditId: response?.audit_id || '',
  operationIdempotencyKey: response?.operation_idempotency_key || '',
});

const driftMappingStepState = ({ status, acknowledged }) => {
  if (status === 'resolved') return 'done';
  if (acknowledged) return 'current';
  return 'pending';
};

export const driftFlowSteps = drift => {
  const status = drift?.status || 'open';
  const acknowledged = ['acknowledged', 'resolved', 'dismissed'].includes(
    status
  );
  const terminal = ['resolved', 'dismissed'].includes(status);

  return [
    { key: 'detected', state: 'done' },
    { key: 'acknowledged', state: acknowledged ? 'done' : 'current' },
    {
      key: 'mapping',
      state: driftMappingStepState({ status, acknowledged }),
    },
    { key: 'replay', state: terminal ? 'current' : 'pending' },
  ];
};

export const normalizeRunsResponse = response => ({
  runs: Array.isArray(response?.payload)
    ? response.payload.map(item => normalizeRun(item))
    : [],
  meta: response?.meta || {
    count: 0,
    total_count: 0,
    page: 1,
    per_page: 50,
    total_pages: 0,
  },
});

export const rawStatusEntries = run =>
  Object.entries(run?.rawStatusCounts || {})
    .map(([status, count]) => ({ status, count: Number(count || 0) }))
    .filter(entry => entry.count > 0);

export const runSourceLabel = run =>
  run?.source?.name || run?.source?.source_key || run?.portal || '-';

export const operationPayload = ({ action, id, reason }) => ({
  reason,
  idempotency_key: `dashboard:listing-import:${action}:${id}`,
});

export const tombstoneCandidateKey = candidate =>
  [
    candidate?.sourceId || candidate?.source_id || 'source',
    candidate?.externalId || candidate?.external_id || 'external',
    candidate?.previousSnapshotId ||
      candidate?.previous_snapshot_id ||
      'snapshot',
  ].join(':');

const stableHash = value => {
  let hash = 0;
  String(value)
    .split('')
    .forEach(character => {
      hash = (hash * 31 + character.charCodeAt(0)) % 1000000007;
    });

  return Math.abs(hash).toString(16).padStart(8, '0');
};

export const tombstoneReviewPayload = ({
  runId,
  decision,
  externalIds = [],
  reason,
}) => {
  const normalizedIds = Array.from(new Set(externalIds.map(String)))
    .map(externalId => externalId.trim())
    .filter(Boolean)
    .sort();
  const operationHash = stableHash(
    JSON.stringify([runId, decision, normalizedIds])
  );

  return {
    external_ids: normalizedIds,
    decision,
    reason,
    idempotency_key: `dashboard:listing-import:tombstone-review:${runId}:${decision}:${operationHash}`,
  };
};

export const driftReviewPayload = ({ action, id, reason, resolution }) => {
  const payload = {
    reason,
    idempotency_key: `dashboard:listing-import-drift:${action}:${id}`,
  };

  if (action === 'resolve') {
    payload.resolution = resolution || 'mapping_fixed';
  }

  return payload;
};

export const normalizeTombstoneReviewResponse = response => ({
  status: response?.status || '',
  reviewCount: Array.isArray(response?.reviews) ? response.reviews.length : 0,
  providerAcquisition: response?.provider_acquisition || 'not_attempted',
  sideEffect: response?.side_effect || 'source_metadata_only',
  operationIdempotencyKey: response?.operation_idempotency_key || '',
});
